import React, { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Result, Button } from 'antd'
import { HomeOutlined, LockOutlined } from '@ant-design/icons'
import { useTranslation } from '../../i18n'
import { getAccessDeniedRedirectElement } from './accessDeniedBehavior'

interface AccessControlledRouteProps {
    canAccess: boolean
    children: ReactNode
    redirectTo?: string
    showAccessDenied?: boolean
    accessDeniedMessage?: string
    accessDeniedDescription?: string
    accessDeniedActionText?: string
}

export const AccessControlledRoute: React.FC<AccessControlledRouteProps> = ({
    canAccess,
    children,
    redirectTo = '/',
    showAccessDenied = true,
    accessDeniedMessage,
    accessDeniedDescription,
    accessDeniedActionText,
}) => {
    const location = useLocation()
    const { t } = useTranslation('common')

    if (canAccess) {
        return <>{children}</>
    }

    const redirectElement = getAccessDeniedRedirectElement(
        location.state,
        redirectTo
    )
    if (redirectElement) {
        return redirectElement
    }

    if (!showAccessDenied) {
        return <Navigate to={redirectTo} replace />
    }

    return (
        <Result
            status="403"
            title={accessDeniedMessage ?? t('accessDenied.defaultTitle')}
            subTitle={accessDeniedDescription ?? t('accessDenied.defaultDescription')}
            icon={<LockOutlined />}
            extra={
                <Button type="primary" icon={<HomeOutlined />} href={redirectTo}>
                    {accessDeniedActionText ?? t('backToHome')}
                </Button>
            }
        />
    )
}
